import React, {FC, useCallback} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownSeparator} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownSeparator';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemChevron} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemChevron';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {AppRoutesPrefixesEnum} from '../../../../libs/shared/ui/src/routing/appRoutesPrefixes';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useAppDispatch} from '../../../helpers/appReduxHelpers';
import {buildContactsManagerAutoContactsUrl} from '../../../helpers/contactsManagerHelpers';
import {isEnhancedGlobalCard, isEnhancedPrivateCard} from '../../../helpers/models/cardHelpers';
import {CardModel, CardTypesEnum} from '../../../models/api/cardModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {fetchContactDetailsDataAction} from '../../../store/panels/contacts/contactDetailsPanelActions';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {ContactCardAccessIndicator} from '../common/contactCardAccessIndicator';
import {useContactDetailsContext} from '../contactDetailsContext';
import {
  buildContactDetailsAddToContactsEvent,
  buildContactDetailsConvertContactTypeEvent,
  ContactDetailsContactTypesEnum,
} from '../contactDetailsTrackingHelpers';
import {ContactHeaderPill} from './contactHeaderPill';

/*
 * Constants.
 */

const DropdownContainerWidth = 260;

/*
 * Props.
 */

interface ContactHeaderVisibilityIndicatorProps {
  contact?: VisualContactModel;
  card: CardModel | undefined;
  additionalCards: ReadonlyArray<CardModel>;
  addToSharedContacts: () => Promise<CardModel | undefined>;
  addToPrivateContacts: () => Promise<CardModel | undefined>;
  convertToGlobalContact: () => Promise<void>;
}

/*
 * Styles.
 */

const StyledIcon = styled(Icon)`
  margin-right: 4px;
`;

const StyledDropdownIcon = styled(Icon)`
  margin-right: 8px;
`;

const StyledDropdownItemContent = styled(DropdownItemContent)`
  display: flex;
  flex-flow: row;
  align-items: center;
`;

const StyledPopoverCoordinator = styled(PopoverCoordinator)`
  display: inline-block;
`;

/*
 * Component.
 */

export const ContactHeaderVisibilityIndicator: FC<ContactHeaderVisibilityIndicatorProps> = (props) => {
  const {card, contact, additionalCards, addToSharedContacts, addToPrivateContacts, convertToGlobalContact} =
    props;
  const {isReadOnly, panelId, onTrackEvent} = useContactDetailsContext();
  // Check for existing cards
  const isSharedCardAlreadyPresent = additionalCards.some((c) => isEnhancedGlobalCard(c));
  const isPrivateCardAlreadyPresent = additionalCards.some((c) => isEnhancedPrivateCard(c));

  const dispatch = useAppDispatch();

  const onNavigate = useCallback(
    (navigateLink?: string) => {
      if (!card) {
        return;
      }
      if (navigateLink) {
        dispatch(navigateToLinkAction(navigateLink));
        return;
      }

      dispatch(
        navigateToLinkAction(`/${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/${card.namespace || ''}`),
      );
    },
    [dispatch, card],
  );

  const onAddToSharedContacts = useCallback(async () => {
    const updatedCard = await addToSharedContacts();
    if (updatedCard && onTrackEvent) {
      onTrackEvent(
        buildContactDetailsAddToContactsEvent(updatedCard, ContactDetailsContactTypesEnum.COMPANY),
      );
    }
  }, [addToSharedContacts, onTrackEvent]);

  const onAddToPrivateContacts = useCallback(async () => {
    const updatedCard = await addToPrivateContacts();
    if (updatedCard && onTrackEvent) {
      onTrackEvent(
        buildContactDetailsAddToContactsEvent(updatedCard, ContactDetailsContactTypesEnum.PRIVATE),
      );
    }
    if (card && contact) {
      dispatch(fetchContactDetailsDataAction(panelId, contact.contact.uid));
    }
  }, [dispatch, addToPrivateContacts, onTrackEvent, card, panelId, contact]);

  const onConvertToGlobalContactAsync = useCallback(async () => {
    await convertToGlobalContact();
    if (card && onTrackEvent) {
      onTrackEvent(
        buildContactDetailsConvertContactTypeEvent(
          ContactDetailsContactTypesEnum.PRIVATE,
          ContactDetailsContactTypesEnum.COMPANY,
          card,
        ),
      );
    }
  }, [card, convertToGlobalContact, onTrackEvent]);
  return maybeRenderVisibilityPill(
    card,
    isSharedCardAlreadyPresent,
    isPrivateCardAlreadyPresent,
    isReadOnly,
    onNavigate,
    onAddToSharedContacts,
    onAddToPrivateContacts,
    onConvertToGlobalContactAsync,
  );
};

const maybeRenderVisibilityPill = (
  card: CardModel | undefined,
  isSharedCardAlreadyPresent: boolean,
  isPrivateCardAlreadyPresent: boolean,
  isReadOnly: boolean,
  onNavigate: () => void,
  onAddToSharedContacts: () => void,
  onAddToPrivateContacts: () => void,
  onConvertToGlobalContactAsync: () => Promise<void>,
) => {
  if (!card) {
    return renderAddToContactsPill(
      isSharedCardAlreadyPresent,
      isPrivateCardAlreadyPresent,
      onAddToSharedContacts,
      onAddToPrivateContacts,
      isReadOnly,
    );
  }

  if (card.type === CardTypesEnum.AUTO) {
    return renderAutoContactPill(
      card,
      onNavigate,
      isSharedCardAlreadyPresent,
      isPrivateCardAlreadyPresent,
      onAddToSharedContacts,
      onAddToPrivateContacts,
      isReadOnly,
    );
  }

  if (isEnhancedPrivateCard(card)) {
    return renderPrivateContactPill(card, onNavigate, onConvertToGlobalContactAsync, isReadOnly);
  }

  if (isEnhancedGlobalCard(card)) {
    return renderCompanyContactPill(card, onNavigate, isReadOnly);
  }

  // Default
  return null;
};

const renderAutoContactPill = (
  card: CardModel,
  onNavigate: (arg: string | undefined) => void,
  isSharedCardAlreadyPresent: boolean,
  isPrivateCardAlreadyPresent: boolean,
  onAddToSharedContacts: () => void,
  onAddToPrivateContacts: () => void,
  isReadOnly: boolean,
) => {
  // If they already have a company card and private card do not render anything
  if (isReadOnly || (isSharedCardAlreadyPresent && isPrivateCardAlreadyPresent)) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledPopoverCoordinator
          interactionId="contact-details-auto-contacts-btn"
          renderButton={(isModalOpen: boolean) => (
            <ContactHeaderPill
              textColor={theme.greys.shade80}
              backgroundColor={isModalOpen ? theme.greys.shade30 : theme.greys.shade20}
              hoverBackgroundColor={theme.greys.shade30}
              isDisabled={isReadOnly}
            >
              <ContactCardAccessIndicator
                card={card}
                iconSize={VisualSizesEnum.SMALL}
                label={<Trans id="contacts-header-auto-contact-label">Auto-created contact</Trans>}
              />
            </ContactHeaderPill>
          )}
          renderModal={(popoverProps) => (
            <Dropdown {...popoverProps} placement="bottom-start" width={DropdownContainerWidth}>
              <DropdownList>
                <DropdownItem
                  key="navigate-key"
                  onSelect={() => onNavigate(buildContactsManagerAutoContactsUrl())}
                >
                  <StyledDropdownItemContent>
                    <StyledDropdownIcon name="sparklesOutlineSmall" color={theme.greys.shade70} />
                    <Trans id="contacts-header-go-to-auto-contacts">Go to auto-created contacts</Trans>
                  </StyledDropdownItemContent>
                  <DropdownItemChevron />
                </DropdownItem>
                <DropdownSeparator />
                {!isSharedCardAlreadyPresent &&
                  renderAddToSharedDropdownItem(() => {
                    onAddToSharedContacts();
                    popoverProps.onRequestClose();
                  }, theme)}

                {/* Only render add to private contact if they do not already have a private card */}
                {!isPrivateCardAlreadyPresent &&
                  renderAddToPrivateDropdownItem(() => {
                    onAddToPrivateContacts();
                    popoverProps.onRequestClose();
                  }, theme)}
              </DropdownList>
            </Dropdown>
          )}
        />
      )}
    </UserThemeConsumer>
  );
};

const renderAddToContactsPill = (
  isSharedCardAlreadyPresent: boolean,
  isPrivateCardAlreadyPresent: boolean,
  onAddToSharedContacts: () => void,
  onAddToPrivateContacts: () => void,
  isReadOnly: boolean,
) => {
  // If they already have a company card and private card do not render anything
  if (isReadOnly || (isSharedCardAlreadyPresent && isPrivateCardAlreadyPresent)) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledPopoverCoordinator
          interactionId="contact-details-add-to-contacts-btn"
          renderButton={(isModalOpen: boolean) => (
            <ContactHeaderPill
              textColor={theme.greys.shade70}
              backgroundColor={isModalOpen ? theme.greys.shade30 : theme.greys.shade20}
              hoverBackgroundColor={theme.greys.shade30}
            >
              <StyledIcon name="newTiny" size={12} color={theme.greys.shade70} />
              <Trans id="contacts-header-add-to-contacts-label">Add to contacts</Trans>
            </ContactHeaderPill>
          )}
          renderModal={(popoverProps) => (
            <Dropdown {...popoverProps} placement="bottom-start" width={DropdownContainerWidth}>
              <DropdownList>
                {/* Only render add to shared contacts if they do not already have a company card */}
                {!isSharedCardAlreadyPresent &&
                  renderAddToSharedDropdownItem(() => {
                    onAddToSharedContacts();
                    popoverProps.onRequestClose();
                  }, theme)}
                {/* Only render add to private contact if they do not already have a private card */}
                {!isPrivateCardAlreadyPresent &&
                  renderAddToPrivateDropdownItem(() => {
                    onAddToPrivateContacts();
                    popoverProps.onRequestClose();
                  }, theme)}
              </DropdownList>
            </Dropdown>
          )}
        />
      )}
    </UserThemeConsumer>
  );
};

const renderPrivateContactPill = (
  card: CardModel,
  onNavigate: () => void,
  onConvertToGlobalContactAsync: () => Promise<void>,
  isReadOnly: boolean,
) => (
  <UserThemeConsumer>
    {(theme) => (
      <StyledPopoverCoordinator
        isDisabled={isReadOnly}
        interactionId="contact-details-private-contact-pill"
        renderButton={(isModalOpen: boolean) => (
          <ContactHeaderPill
            textColor={theme.greys.shade80}
            backgroundColor={isModalOpen ? theme.greys.shade30 : theme.greys.shade20}
            hoverBackgroundColor={theme.greys.shade30}
            isDisabled={isReadOnly}
          >
            <ContactCardAccessIndicator
              card={card}
              iconSize={VisualSizesEnum.SMALL}
              label={<Trans id="contacts-header-private-contact-label">Private contact</Trans>}
            />
          </ContactHeaderPill>
        )}
        renderModal={(popoverProps) => (
          <Dropdown {...popoverProps} placement="bottom-start" width={DropdownContainerWidth}>
            <DropdownList>
              <DropdownItem key="navigate-key" onSelect={() => onNavigate()}>
                <StyledDropdownItemContent>
                  <StyledDropdownIcon name="lockOutlineSmall" size={16} color={theme.greys.shade70} />
                  <Trans id="contacts-header-go-to-private-contacts">Go to private contacts</Trans>
                </StyledDropdownItemContent>
                <DropdownItemChevron />
              </DropdownItem>
              <DropdownSeparator />
              <DropdownItem
                onSelect={() => {
                  onConvertToGlobalContactAsync();
                  popoverProps.onRequestClose();
                }}
              >
                <StyledDropdownItemContent>
                  <StyledDropdownIcon name="sharedOutlineSmall" size={16} color={theme.greys.shade70} />
                  <Trans id="contacts-header-convert-to-shared-contact">Convert to shared contact</Trans>
                </StyledDropdownItemContent>
              </DropdownItem>
            </DropdownList>
          </Dropdown>
        )}
      />
    )}
  </UserThemeConsumer>
);

const renderCompanyContactPill = (card: CardModel, onNavigate: () => void, isReadOnly: boolean) => (
  <UserThemeConsumer>
    {(theme) => (
      <StyledPopoverCoordinator
        isDisabled={isReadOnly}
        interactionId="contact-details-shared-contact-pill"
        renderButton={(isModalOpen: boolean) => (
          <ContactHeaderPill
            textColor={theme.greys.shade80}
            backgroundColor={isModalOpen ? theme.greys.shade30 : theme.greys.shade20}
            hoverBackgroundColor={theme.greys.shade30}
            isDisabled={isReadOnly}
          >
            <ContactCardAccessIndicator
              card={card}
              iconSize={VisualSizesEnum.SMALL}
              label={<Trans id="contacts-header-shared-contact-label">Shared contact</Trans>}
            />
          </ContactHeaderPill>
        )}
        renderModal={(popoverProps) => (
          <Dropdown {...popoverProps} placement="bottom-start" width={DropdownContainerWidth}>
            <DropdownList>
              <DropdownItem key="navigate-key" onSelect={() => onNavigate()}>
                <StyledDropdownItemContent>
                  <StyledDropdownIcon name="sharedOutlineSmall" size={16} color={theme.greys.shade70} />
                  <Trans id="contacts-header-go-to-shared-contacts">Go to shared contacts</Trans>
                </StyledDropdownItemContent>
                <DropdownItemChevron />
              </DropdownItem>
            </DropdownList>
          </Dropdown>
        )}
      />
    )}
  </UserThemeConsumer>
);

const renderAddToPrivateDropdownItem = (onSelect: () => void, theme: UserTheme) => (
  <DropdownItem
    key="add-to-private-key"
    onSelect={onSelect}
    interactionId="contact-details-add-to-private-contacts-option"
  >
    <StyledDropdownItemContent>
      <StyledDropdownIcon name="lockOutlineSmall" size={16} color={theme.greys.shade70} />
      <Trans id="contacts-header-add-to-private-contacts">Add to private contacts</Trans>
    </StyledDropdownItemContent>
  </DropdownItem>
);

const renderAddToSharedDropdownItem = (onSelect: () => void, theme: UserTheme) => (
  <DropdownItem
    key="add-to-company-key"
    onSelect={onSelect}
    interactionId="contact-details-add-to-company-contacts-option"
  >
    <StyledDropdownItemContent>
      <StyledDropdownIcon name="sharedOutlineSmall" color={theme.greys.shade70} />
      <Trans id="contacts-header-add-to-shared-contacts">Add to shared contacts</Trans>
    </StyledDropdownItemContent>
  </DropdownItem>
);
